let calendlyPromise;

export const CALENDLY_URL = process.env.REACT_APP_CALENDLY_URL;

export function loadCalendlyScript() {
  if (window.Calendly) return Promise.resolve(window.Calendly);
  if (calendlyPromise) return calendlyPromise;

  calendlyPromise = new Promise((resolve, reject) => {
    const existing = document.querySelector(
      'script[src="https://assets.calendly.com/assets/external/widget.js"]'
    );
    if (existing) {
      existing.addEventListener("load", () => resolve(window.Calendly));
      existing.addEventListener("error", reject);
      return;
    }
    const script = document.createElement("script");
    script.src = "https://assets.calendly.com/assets/external/widget.js";
    script.async = true;
    script.onload = () => resolve(window.Calendly);
    script.onerror = () => {
      calendlyPromise = undefined;
      reject(new Error("Unable to load the scheduling widget"));
    };
    document.head.appendChild(script);
  });

  return calendlyPromise;
}

export async function openCalendlyPopup() {
  const Calendly = await loadCalendlyScript();
  Calendly.initPopupWidget({ url: CALENDLY_URL });
}


// ---- Server-side booking attribution ----
// Persists landing-page attribution (UTMs, gclid) and mints a one-time booking
// token tied to the visitor's GA4 client/session ids. The token travels through
// Calendly as utm_content so the server-side webhook can fire generate_lead
// against the correct GA4 session. If anything fails, the embed still works.

const ATTR_KEYS = ["utm_source", "utm_medium", "utm_campaign", "utm_term", "gclid"];

function captureLandingAttribution() {
  try {
    const params = new URLSearchParams(window.location.search);
    const found = {};
    ATTR_KEYS.forEach((k) => {
      const v = params.get(k);
      if (v) found[k] = v;
    });
    if (Object.keys(found).length > 0) {
      sessionStorage.setItem("hlt-attr", JSON.stringify(found));
    }
  } catch (e) {
    /* storage unavailable */
  }
}

function getStoredAttribution() {
  try {
    return JSON.parse(sessionStorage.getItem("hlt-attr") || "{}");
  } catch (e) {
    return {};
  }
}

let tokenPromise = null;

export function getBookingContext() {
  captureLandingAttribution();
  if (!tokenPromise) {
    tokenPromise = new Promise((resolve) => {
      const GA_ID = process.env.REACT_APP_GA_ID;
      let attempts = 0;
      const tryGet = () => {
        attempts += 1;
        if (window.gtag && GA_ID) {
          window.gtag("get", GA_ID, "client_id", (clientId) => {
            window.gtag("get", GA_ID, "session_id", (sessionId) => {
              const attr = getStoredAttribution();
              fetch(`${process.env.REACT_APP_BACKEND_URL}/api/tracking/session`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                  client_id: clientId || null,
                  session_id: sessionId ? String(sessionId) : null,
                  gclid: attr.gclid || null,
                  utm_source: attr.utm_source || null,
                  utm_medium: attr.utm_medium || null,
                  utm_campaign: attr.utm_campaign || null,
                  landing_path: window.location.pathname,
                }),
              })
                .then((r) => (r.ok ? r.json() : null))
                .then((d) => resolve(d?.token || null))
                .catch(() => resolve(null));
            });
          });
        } else if (attempts < 20) {
          setTimeout(tryGet, 300);
        } else {
          resolve(null);
        }
      };
      tryGet();
    });
  }
  return tokenPromise;
}

export async function buildCalendlyEmbedUrl(baseSrc) {
  const token = await getBookingContext();
  if (!token) return baseSrc;
  const attr = getStoredAttribution();
  let url = baseSrc;
  ["utm_source", "utm_medium", "utm_campaign"].forEach((k) => {
    if (attr[k]) url += `&${k}=${encodeURIComponent(attr[k])}`;
  });
  url += `&utm_content=${encodeURIComponent(token)}`;
  return url;
}